import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, provideZonelessChangeDetection } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter, withComponentInputBinding, withViewTransitions } from '@angular/router';

import {
  ProfileService,
  acceptLanguageInterceptor,
  provideProfileInitializer,
  withApiErrorInterceptor,
  withCredentialsInterceptor,
  withCustomHeaderInterceptor,
  withUnauthorizedInterceptor,
} from '@tfs/auth';
import { provideNotificationsHub } from '@tfs/notifications';
import { provideTfsDateAdapter } from '@tfs/shared-ui/ui-common/date-adapter';
import { provideToolImportDataSync } from '@tfs/tool-import-status';
import {
  provideApi as provideBffApi,
  withBackendApiConfiguration as withBffApiConfiguration,
} from '@tfs/tool-library-bff-api';
import {
  provideApi as provideDataApi,
  withBackendApiConfiguration as withDataApiConfiguration,
} from '@tfs/tool-library-data-api';
import {
  CampaignsApi,
  provideApi as provideLibraryApi,
  withBackendApiConfiguration as withLibraryApiConfiguration,
} from '@tfs/tool-library-library-api';
import {
  provideApi as provideSearchApi,
  withBackendApiConfiguration as withSearchApiConfiguration,
} from '@tfs/tool-library-search-api';
import { TOOL_LIBRARY_BASE_API_URL } from '@tfs/tool-library-tokens';

import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../environment.config';

import { appRoutes } from './app.routes';
import {
  NPS_CAMPAIGN_BACKEND,
  NpsCampaignRealBackend,
  NpsCampaignSimulator,
} from './feedback-widget/nps-campaign-backend';

export const appConfig = (environmentConfig: EnvironmentConfig): ApplicationConfig => ({
  providers: [
    { provide: ENVIRONMENT_CONFIG, useValue: environmentConfig },
    {
      provide: TOOL_LIBRARY_BASE_API_URL,
      useFactory: (config: EnvironmentConfig) => config.toolLibraryBaseApiUrl,
      deps: [ENVIRONMENT_CONFIG],
    },
    provideZonelessChangeDetection(),

    provideBffApi(withBffApiConfiguration({ basePath: environmentConfig.toolLibraryBaseApiUrl })),
    provideLibraryApi(
      withLibraryApiConfiguration({
        basePath: `${environmentConfig.toolLibraryBaseApiUrl}/library`,
      })
    ),
    provideSearchApi(
      withSearchApiConfiguration({ basePath: `${environmentConfig.toolLibraryBaseApiUrl}/search` })
    ),
    provideDataApi(
      withDataApiConfiguration({ basePath: `${environmentConfig.toolLibraryBaseApiUrl}/data` })
    ),
    provideRouter(appRoutes, withComponentInputBinding(), withViewTransitions()),
    provideHttpClient(
      withInterceptors([
        withCredentialsInterceptor([environmentConfig.toolLibraryBaseApiUrl]),
        acceptLanguageInterceptor([environmentConfig.toolLibraryBaseApiUrl]),
        withCustomHeaderInterceptor,
        withUnauthorizedInterceptor,
        withApiErrorInterceptor,
      ])
    ),
    provideProfileInitializer(),
    provideNotificationsHub(),
    provideToolImportDataSync(),
    {
      provide: NPS_CAMPAIGN_BACKEND,
      useFactory: (
        config: EnvironmentConfig,
        campaignsApi: CampaignsApi,
        profileService: ProfileService
      ) => {
        if (config.nps?.simulator) {
          return new NpsCampaignSimulator(config.nps.simulator);
        }

        return new NpsCampaignRealBackend(campaignsApi, profileService);
      },
      deps: [ENVIRONMENT_CONFIG, CampaignsApi, ProfileService],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always' },
    },
    ...provideTfsDateAdapter(),
  ],
});
